import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Form, Grid, Input, InputNumber, Select, Space, Table, Typography } from "antd";
import { t } from "i18next";
import moment from "moment";
import ApiService from "../../../core/service/ApiService";
import DatePicker from "../../../core/components/DatePicker";
import FileUtils from "../../../core/utils/file-utils";
import Helper from "../../../core/Helper";
import { useUser } from "../../../core/context/UserContext";

const { Text } = Typography;

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export default function InvoiceForm({ appointments, onSaved }) {
    const { formatPrice } = useUser();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [excludedIds, setExcludedIds] = useState([]);
    const [file, setFile] = useState(null);
    const [lines, setLines] = useState([]);
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;
    const isBulk = appointments.length > 1;
    const currency = appointments[0]?.currency || 'TRY';

    useEffect(() => {
        const load = async () => {
            const results = await Promise.all(appointments.map(x => ApiService.Invoice.GetAppointmentInvoices(x.appointmentId)));
            const allRows = appointments.map((appointment, index) => {
                const invoices = results[index]?.success ? results[index].data : [];
                const invoiced = invoices.filter(x => !x.isDeleted).reduce((sum, x) => sum + x.appointmentAmount, 0);
                const remaining = Math.max(round2(appointment.totalPrice - invoiced), 0);
                return { ...appointment, remaining, amount: remaining };
            });
            const nextRows = isBulk ? allRows.filter(x => x.remaining > 0) : allRows;
            setExcludedIds(isBulk ? allRows.filter(x => x.remaining <= 0).map(x => x.appointmentId) : []);
            setRows(nextRows);
            form.setFieldsValue({ totalAmount: round2(nextRows.reduce((sum, x) => sum + x.amount, 0)) });
        };
        load();

        if (!isBulk) {
            const loadLines = async () => {
                const res = await ApiService.Appointment.GetAppointment(appointments[0].appointmentId);
                if (res.success) {
                    setLines([
                        ...res.data.appointmentOperations.map(x => ({ value: `operation-${x.id}`, label: x.operation?.name })),
                        ...res.data.appointmentOtherOperations.map(x => ({ value: `other-${x.id}`, label: x.name })),
                    ]);
                }
            };
            loadLines();
        }
    }, [appointments]);

    const distributed = useMemo(() => round2(rows.reduce((sum, x) => sum + (x.amount || 0), 0)), [rows]);
    const totalAmount = Form.useWatch('totalAmount', form);

    const setRowAmount = (appointmentId, amount) => {
        setRows(rows.map(x => x.appointmentId === appointmentId ? { ...x, amount } : x));
    };

    const selectFile = async () => {
        const selected = await FileUtils.getFileAsBase64({ accept: '.pdf,.jpg,.jpeg,.png' });
        setFile(selected);
    };

    const amountColumn = {
        key: 'amount', title: t('Amount'), width: isMobile ? 120 : 130, render: (_, row) => (
            <InputNumber size="small" min={0} style={{ width: '100%' }} value={row.amount} onChange={(value) => setRowAmount(row.appointmentId, value)} />
        )
    };

    const bulkColumns = isMobile
        ? [
            {
                key: 'appointment', title: t('Appointment'), render: (_, row) => (
                    <div style={{ minWidth: 0 }}>
                        <Text strong ellipsis style={{ display: 'block' }}>#{row.appointmentId} {row.patientName} {row.patientLastname}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Remaining')}: {formatPrice(row.remaining, { currency })}</Text>
                    </div>
                )
            },
            amountColumn,
        ]
        : [
            { key: 'appointmentId', dataIndex: 'appointmentId', title: t('App. ID'), width: 90 },
            { key: 'patient', title: t('Patient'), ellipsis: true, render: (_, row) => `${row.patientName} ${row.patientLastname}` },
            { key: 'remaining', dataIndex: 'remaining', title: t('Remaining'), align: 'right', width: 110, render: (value) => formatPrice(value, { currency }) },
            amountColumn,
        ];

    const onFinish = async (values) => {
        const lineValue = values.line || '';
        const res = await ApiService.Invoice.CreateManualInvoice({
            totalAmount: values.totalAmount,
            invoiceDate: values.invoiceDate,
            invoiceNo: values.invoiceNo,
            description: values.description,
            isOss: !!values.isOss,
            file: file ? { originalFileName: file.fileName, base64Data: file.data, fileType: 3 } : null,
            items: rows.map(x => ({
                appointmentId: x.appointmentId,
                amount: isBulk ? x.amount : values.totalAmount,
                appointmentOperationId: lineValue.startsWith('operation-') ? Number(lineValue.split('-')[1]) : null,
                appointmentOtherOperationId: lineValue.startsWith('other-') ? Number(lineValue.split('-')[1]) : null,
            })),
        });

        if (res.success) {
            onSaved && onSaved();
        }
    };

    return (
        <Form form={form} layout={'vertical'} size={'small'} onFinish={onFinish} initialValues={{ invoiceDate: moment(), isOss: false }}>
            <Form.Item name={'totalAmount'} label={t('Amount')} rules={[{ required: true, message: t('Required') }]}>
                <InputNumber min={0} style={{ width: '100%' }} addonAfter={currency} />
            </Form.Item>
            <Form.Item name={'invoiceDate'} label={t('Invoice Date')} rules={[{ required: true, message: t('Required') }]}>
                <DatePicker format={'DD/MM/YYYY'} style={{ width: '100%' }} locale={Helper.Date.getLocale()} />
            </Form.Item>
            <Form.Item name={'invoiceNo'} label={t('Invoice No')}>
                <Input maxLength={100} />
            </Form.Item>
            <Form.Item name={'description'} label={t('Description')}>
                <Input maxLength={500} />
            </Form.Item>
            <Form.Item name={'isOss'} valuePropName={'checked'}>
                <Checkbox>{t('OSS Invoice')}</Checkbox>
            </Form.Item>
            {!isBulk && lines.length > 0 && (
                <Form.Item name={'line'} label={t('Invoice Line')}>
                    <Select allowClear options={lines} placeholder={t('All Operations')} />
                </Form.Item>
            )}

            {excludedIds.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${t('Appointments with no remaining amount were left out')}: ${excludedIds.map(x => `#${x}`).join(', ')}`}
                />
            )}

            {isBulk && (
                <>
                    <Table
                        size="small"
                        rowKey="appointmentId"
                        pagination={false}
                        dataSource={rows}
                        scroll={{ y: 260 }}
                        tableLayout="fixed"
                        columns={bulkColumns}
                    />
                    <div style={{ textAlign: 'right', margin: '8px 0' }}>
                        <Text type={Math.abs(distributed - (totalAmount || 0)) > 0.01 ? 'danger' : 'secondary'}>
                            {t('Distributed Amount')}: {formatPrice(distributed, { currency })}
                        </Text>
                    </div>
                </>
            )}

            <Space style={{ marginBottom: 12 }}>
                <Button size="small" onClick={selectFile}>{t('Select File')}</Button>
                {file && <Text>{file.fileName}</Text>}
            </Space>
            {!file && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('No file attached, you can add it later.')} />
            )}

            <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" disabled={rows.length === 0}>{t('Save')}</Button>
            </Form.Item>
        </Form>
    );
}
